import { Button, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';

import { useLocalStorage } from '../../services/hooks/useLocalStorage';

import Loading from '../Loading';

import EmptyTargetBig from '../../img/empty-target.png';
import EmptyCircle from '../../img/circles-graphic-masked.png';
import KeyHand from '../../img/key-hand.png';
import KeyHandSmall from '../../img/key-hand-small.png';
import KeyHouse from '../../img/tenant-key.png';
import STCLogo from '../../img/small-body-logo-stc.png';
import STCLogoBig from '../../img/body-logo-stc.png';
import StratusLogo from '../../img/small-body-logo-stratus.png';
import StratusLogoBig from '../../img/body-logo-stratus.png';
import Swoosh from '../../img/purple-swoosh.png';
import SwooshBig from '../../img/purple-swoosh-big.png';

const useStyles = makeStyles({
    box: {
        display: 'grid',
        gridTemplateRows: '49px 486px',
        gridTemplateColumns: '368px auto',
        gridTemplateAreas: '"header header" "card content"',
        overflow: 'hidden',
        '@media (max-width: 800px)': {
            gridTemplateColumns: '238px auto',
        },
    },
    disabled_text: {
        color: '#32323281',
    },
    empty_tenant_container: {
        display: "flex",
        flexDirection: "row",
        alignContent: "center",
        justifyContent: "center",
        alignItems: "center",
        margin: "10%"
    },
    empty_tenant_text_container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center"
    },
    empty_tenant_header: {
        fontSize: "2em",
    },
    empty_tenant_text: {
        margin: "8px 0",
        textAlign: "center",
        fontSize: "1.4em",
    },
    left_logo: {
        display: 'flex',
        height: 'calc(100vh - 66px)',
        alignItems: 'center',
    },
    logo: {
        marginRight: '30px',
        maxWidth: '450px',
        '@media (max-width: 800px)': {
            marginRight: '0',
            maxWidth: '378px',
        },
    },
    right_action: {
        display: 'grid',
        backgroundColor: '#5F3FA5',
        paddingRight: '35px',
        paddingTop: '60px',
        gridTemplateRows: '140px 70px 28px 32px',
        minHeight: '486px',
        overflow: 'hidden',
        color: 'white',
        justifyItems: 'center'
    },
    right_no_tenant: {
        display: 'grid',
        backgroundColor: '#5F3FA5',
        paddingRight: '35px',
        paddingTop: '60px',
        gridTemplateRows: '140px 54px 80px 60px',
        minHeight: '486px',
        overflow: 'hidden',
        color: 'white',
        justifyItems: 'center'
    },
    swoosh: {
        backgroundImage: `url(${Swoosh})`,
        height: "486px",
        width: "78px",
        position: 'absolute',
        top: '-7px',
        left: '292px',
        '@media (max-width: 800px)': {
            left: '162px',
        },
    },
    swoosh_big: {
        backgroundImage: `url(${SwooshBig})`,
        height: "535px",
        width: "74px",
        position: 'absolute',
        top: '1px',
        left: '296px'
    },
    target_left: {
        backgroundImage: `url(${EmptyTargetBig})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        zIndex: '-1',
        width: '370px',
        height: '450px',
        '@media (max-width: 800px)': {
            width: '282px',
            height: '322px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
        },
    },
    target_left_empty: {
        backgroundImage: `url(${EmptyCircle})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'absolute',
        zIndex: '-1',
        width: '370px',
        height: '450px',
        '@media (max-width: 800px)': {
            width: '282px',
            height: '322px',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
        },
    }
});

const TenantSelector = (props) => {
    const [tenant, setTenant] = useState('');
    const [stratus] = useLocalStorage("stratus", false);
    const classes = useStyles();
    const ns = stratus ? 'stratus' : 'scantrip';
    const { t } = useTranslation(ns);

    const handleChange = (event) => {
        setTenant(event.target.value);
    }

    if (props.loading) {
        return <Loading />
    }


    if (props.tenants && props.tenants?.length > 0) {
        const logo = window.innerWidth > 800 ? (stratus ? StratusLogoBig : STCLogoBig) : (stratus ? StratusLogo : STCLogo);
        const key = window.innerWidth > 800 ? KeyHand : KeyHandSmall;
        return (
            <div className={classes.box}>
                <div id="left-logo" className={classes.left_logo}>
                    <div className={classes.target_left}>
                        <img src={key} alt="Hand holding a key" />
                    </div>
                    <div id="swoosh" className={window.innerWidth > 1000 ? classes.swoosh_big : classes.swoosh}></div>
                </div>
                <div id="right-action" className={classes.right_action}>
                    <img src={logo} className={classes.logo} alt="Dispatcher ScanTrip Cloud" />
                    <InputLabel id="tenant-select-label" sx={{ color: 'white' }}>{t('cognito:tenant_select')}</InputLabel>
                    <InputLabel id="tenant-label" sx={{ color: 'white', fontSize: '19px', marginRight: '360px' }}>{t('Tenant')}</InputLabel>
                    <Select
                        labelId="tenant-select-label"
                        id="tenant-select"
                        value={tenant}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(selected) => {
                            if (selected.length === 0) {
                                return <Typography className={classes.disabled_text}>{t('cognito:tenant_select_dropdown')}</Typography>
                            }
                            return selected.name
                        }}
                        sx={{
                            background: 'white',
                            width: '417px',
                            height: '31px',
                            '& legend': { display: 'none' },
                            '& fieldset': { top: 0 },
                            '& svg': { color: 'black' }
                        }}
                    >
                        {props?.tenants?.map(tenant => {
                            return <MenuItem value={tenant} key={tenant.slug}>{tenant.name}</MenuItem>
                        })}
                    </Select>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => props.onClick(tenant)}
                        sx={{
                            color: "white !important",
                            width: "90px",
                            height: "36px",
                            margin: "32px",
                            backgroundColor: "#272261"
                        }}
                        disabled={tenant === ''}
                    >
                        {t('OK')}
                    </Button>
                </div>
            </div>
        );
    } else if (props.tenants) {
        const logo = window.innerWidth > 800 ? (stratus ? StratusLogoBig : STCLogoBig) : (stratus ? StratusLogo : STCLogo);
        const key = KeyHouse;
        return (
            <div className={classes.box}>
                <div id="left-logo" className={classes.left_logo}>
                    <div className={classes.target_left_empty}>
                        <img src={key} alt="Key in a house" />
                    </div>
                    <div id="swoosh" className={window.innerWidth > 1000 ? classes.swoosh_big : classes.swoosh}></div>
                </div>
                <div id="right-action" className={classes.right_no_tenant}>
                    <img src={logo} className={classes.logo} alt="Dispatcher ScanTrip Cloud" />
                    <div className={classes.empty_tenant_header} >{t('No Tenant Found')}</div>
                    <div className={classes.empty_tenant_text} >{t('tenantselector:no_tenant_line_one')}</div>
                    <div className={classes.empty_tenant_text} >{t('tenantselector:no_tenant_line_two')}</div>
                </div>
            </div>
        );
    }
    return <Loading />
}

export default TenantSelector